import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import LogService from '../services/LogService';
import { TopDataResponse, TimeFilter } from '../types/LogTypes';
import './Top.css';

const Top: React.FC = () => {
  const navigate = useNavigate();
  const [topData, setTopData] = useState<TopDataResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [timeFilter, setTimeFilter] = useState<TimeFilter>('today');

  const loadTopData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await LogService.getTopData(timeFilter);
      setTopData(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load top data');
    } finally {
      setLoading(false);
    }
  }, [timeFilter]);

  useEffect(() => {
    loadTopData();
  }, [loadTopData]);

  const getTimeFilterLabel = (filter: TimeFilter): string => {
    switch (filter) {
      case 'lastHour':
        return 'Last Hour';
      case 'last4Hours':
        return 'Last 4 Hours';
      case 'today':
        return 'Today';
      case 'yesterday':
        return 'Yesterday';
      case 'thisWeek':
        return 'This Week';
      case 'last7Days':
        return 'Last 7 Days';
      default:
        return filter;
    }
  };

  const formatDateTime = (dateStr: string): string => {
    const date = new Date(dateStr);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleMessageClick = (message: string) => {
    // Navigate to logs page with message search filter
    navigate(`/logs?searchText=${encodeURIComponent(message)}`);
  };

  const handleCustomerServiceEnvironmentClick = (customer: string, service: string, environment: string) => {
    // Navigate to logs page with customer, service, and environment filters
    const params = new URLSearchParams({
      customer,
      service,
      environment,
    });
    navigate(`/logs?${params.toString()}`);
  };

  const handleLogTypeClick = (logType: string) => {
    // Navigate to logs page with logType filter
    navigate(`/logs?logType=${encodeURIComponent(logType)}`);
  };

  if (loading) {
    return <div className="top-loading">Loading top statistics...</div>;
  }

  if (error) {
    return (
      <div className="top-error">
        <p>Error: {error}</p>
        <button onClick={loadTopData}>Retry</button>
      </div>
    );
  }

  if (!topData) {
    return <div className="top-error">No data available</div>;
  }

  return (
    <div className="top">
      <h1>Top Statistics</h1>

      <div className="time-filter-section">
        <label htmlFor="timeFilter">Time Period:</label>
        <select
          id="timeFilter"
          value={timeFilter}
          onChange={(e) => setTimeFilter(e.target.value as TimeFilter)}
          className="time-filter-select"
        >
          <option value="lastHour">Last Hour</option>
          <option value="last4Hours">Last 4 Hours</option>
          <option value="today">Today</option>
          <option value="yesterday">Yesterday</option>
          <option value="thisWeek">This Week</option>
          <option value="last7Days">Last 7 Days</option>
        </select>
      </div>

      <div className="top-summary">
        <div className="summary-item">
          <div className="summary-label">Total Logs</div>
          <div className="summary-value">{topData.totalLogs.toLocaleString()}</div>
        </div>
        <div className="summary-item">
          <div className="summary-label">Period</div>
          <div className="summary-value">{getTimeFilterLabel(timeFilter)}</div>
        </div>
        <div className="summary-item">
          <div className="summary-label">From</div>
          <div className="summary-value">{formatDateTime(topData.startTime)}</div>
        </div>
        <div className="summary-item">
          <div className="summary-label">To</div>
          <div className="summary-value">{formatDateTime(topData.endTime)}</div>
        </div>
      </div>

      <div className="top-sections">
        {/* Top Messages */}
        <div className="top-section">
          <h2>Top Messages</h2>
          <div className="top-table-container">
            <table className="top-table">
              <thead>
                <tr>
                  <th className="rank-column">#</th>
                  <th>Log Type</th>
                  <th className="message-column">Message</th>
                  <th className="count-column">Count</th>
                </tr>
              </thead>
              <tbody>
                {topData.topMessages.length > 0 ? (
                  topData.topMessages.map((item, index) => (
                    <tr 
                      key={index} 
                      className="clickable-row"
                      onClick={() => handleMessageClick(item.message)}
                      title="Click to view logs with this message"
                    >
                      <td className="rank-column">{index + 1}</td>
                      <td>{item.logType}</td>
                      <td className="message-column" title={item.message}>
                        {item.message}
                      </td>
                      <td className="count-column">{item.count.toLocaleString()}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={4} className="no-data">No messages found</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Top Customer/Service/Environment Combinations */}
        <div className="top-section">
          <h2>Top Customer / Service / Environment</h2>
          <div className="top-table-container">
            <table className="top-table">
              <thead>
                <tr>
                  <th className="rank-column">#</th>
                  <th>Customer</th>
                  <th>Service</th>
                  <th>Environment</th>
                  <th className="count-column">Count</th>
                </tr>
              </thead>
              <tbody>
                {topData.topCustomerServiceEnvironments.length > 0 ? (
                  topData.topCustomerServiceEnvironments.map((item, index) => (
                    <tr 
                      key={index}
                      className="clickable-row"
                      onClick={() => handleCustomerServiceEnvironmentClick(item.customer, item.service, item.environment)}
                      title="Click to view logs for this customer/service/environment"
                    >
                      <td className="rank-column">{index + 1}</td>
                      <td>{item.customer}</td>
                      <td>{item.service}</td>
                      <td>{item.environment}</td>
                      <td className="count-column">{item.count.toLocaleString()}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={5} className="no-data">No data found</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Top Log Types */}
        <div className="top-section">
          <h2>Top Log Types</h2>
          <div className="top-table-container">
            <table className="top-table">
              <thead>
                <tr>
                  <th className="rank-column">#</th>
                  <th>Log Type</th>
                  <th className="count-column">Count</th>
                  <th className="percentage-column">%</th>
                </tr>
              </thead>
              <tbody>
                {topData.topLogTypes.length > 0 ? (
                  topData.topLogTypes.map((item, index) => (
                    <tr 
                      key={index}
                      className="clickable-row"
                      onClick={() => handleLogTypeClick(item.logType)}
                      title="Click to view logs of this type"
                    >
                      <td className="rank-column">{index + 1}</td>
                      <td>{item.logType}</td>
                      <td className="count-column">{item.count.toLocaleString()}</td>
                      <td className="percentage-column">
                        {topData.totalLogs > 0
                          ? ((item.count / topData.totalLogs) * 100).toFixed(1)
                          : '0.0'}%
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={4} className="no-data">No log types found</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="refresh-section">
        <button onClick={loadTopData} className="refresh-button">
          Refresh Data
        </button>
      </div>
    </div>
  );
};

export default Top;
